// 確定後（発注済み・一部入荷）の発注書をその場で直す編集面。
//
// 🔴 発注数は入荷済みの数量より下げられない（backend の床＝手前でも同じ理由を出す）。
// 🔴 行の削除は未入荷の明細だけ・最後の1行は消せない（全部やめるならキャンセル）。
// 🔑 追加は入荷と同じスキャン/SKU 入力＝数量1で入り、数量欄で直す（picker を増やさない）。
// 🔑 派生値（発注提案の差引・実原価の配賦）はサーバー側でライブ計算＝保存後に
//    関連クエリを無効化するだけで全部ついてくる。
import {
  Banner,
  BlockStack,
  Button,
  InlineGrid,
  InlineStack,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import {
  addPlacedLine,
  removePlacedLine,
  updatePlacedLine,
  updatePlacedOrder,
  type PlacedOrder,
  type PlacedOrderItem,
} from "../../api/purchase_order_lifecycle";
import { type VariantMatch } from "../../api/variants";
import { ApiError } from "../../api/client";
import { useVerifiedShopGeneration } from "../../ShopGenerationBoundary";
import { ScanInput } from "../ScanInput";
import {
  clearPersistentIdempotencyAttempt,
  persistentIdempotencyAttempt,
  type PersistentIdempotencyAttempt,
} from "../../utils/idempotency";

import { t } from "../../lib/i18n";

interface Props {
  po: PlacedOrder;
}

function isDefinitiveAddFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return (
    error.status >= 400 &&
    error.status < 500 &&
    ![408, 409, 425, 429].includes(error.status)
  );
}

export function PlacedOrderEditPanel({ po }: Props) {
  const queryClient = useQueryClient();
  const { shopGeneration } = useVerifiedShopGeneration();
  const [error, setError] = useState<string | null>(null);
  const addAttempt = useRef<PersistentIdempotencyAttempt | null>(null);
  const addAttemptScope = `purchase-order:${po.id}:add-line`;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["placed-orders"] });
    // 数量・行の増減で open qty が変わる＝発注提案の数量も変わる。
    queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });
    // 明細が変われば諸掛の配賦も引き直される。
    queryClient.invalidateQueries({ queryKey: ["landed-cost"] });
  };

  const onMutationError = (err: Error) => setError(err.message);

  const lineMutation = useMutation({
    mutationFn: (args: {
      itemId: string;
      input: { ordered_qty?: number; unit_price?: string };
    }) => updatePlacedLine(po.id, args.itemId, args.input),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const headerMutation = useMutation({
    mutationFn: (input: {
      note?: string | null;
      expected_at?: string | null;
    }) => updatePlacedOrder(po.id, input),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const addMutation = useMutation({
    mutationFn: ({
      input,
      attempt,
    }: {
      input: Parameters<typeof addPlacedLine>[1];
      attempt: PersistentIdempotencyAttempt;
    }) => addPlacedLine(po.id, input, attempt.key),
    onSuccess: (_po, variables) => {
      clearPersistentIdempotencyAttempt(addAttemptScope, variables.attempt);
      addAttempt.current = null;
      invalidate();
    },
    onError: (err: Error, variables) => {
      if (isDefinitiveAddFailure(err)) {
        clearPersistentIdempotencyAttempt(addAttemptScope, variables.attempt);
        addAttempt.current = null;
      }
      onMutationError(err);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (itemId: string) => removePlacedLine(po.id, itemId),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const validateAddition = (variant: VariantMatch): string | null => {
    if (
      variant.sku !== null &&
      po.items.some((item) => item.sku === variant.sku)
    ) {
      // 同じ商品の2行目を黙って作らない＝数量を直す導線を返す。
      return t(
        "{item} は既にこの発注書にあります。数量欄で増やしてください。",
        {
          item: variant.sku,
        },
      );
    }
    return null;
  };

  return (
    <BlockStack gap="300">
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <InlineStack gap="300" wrap>
        <div style={{ minWidth: 240, flex: 1 }}>
          {/* key＝サーバ値：保存やリロードで値が変わったら入力欄ごと作り直す
              （手元の編集中の値とサーバ値の二重管理をしない）。 */}
          <HeaderTextField
            key={`note:${po.note ?? ""}`}
            label={t("メモ")}
            value={po.note}
            onCommit={(next) => headerMutation.mutate({ note: next })}
          />
        </div>
        <div style={{ width: 180 }}>
          <HeaderTextField
            key={`expected:${po.expected_at ?? ""}`}
            label={t("入荷予定日")}
            type="date"
            value={po.expected_at}
            onCommit={(next) => headerMutation.mutate({ expected_at: next })}
          />
        </div>
      </InlineStack>

      <BlockStack gap="200">
        {po.items.map((item) => (
          <EditableLineRow
            key={`${item.id}:${item.ordered_qty}:${item.unit_price}`}
            item={item}
            canRemove={item.received_qty === 0 && po.items.length > 1}
            isBusy={lineMutation.isPending || removeMutation.isPending}
            onCommitQty={(qty) =>
              lineMutation.mutate({
                itemId: item.id,
                input: { ordered_qty: qty },
              })
            }
            onCommitPrice={(price) =>
              lineMutation.mutate({
                itemId: item.id,
                input: { unit_price: price },
              })
            }
            onRemove={() => {
              if (
                window.confirm(
                  t("{item} をこの発注書から削除しますか？", {
                    item: item.sku ?? item.title ?? item.id,
                  }),
                )
              ) {
                removeMutation.mutate(item.id);
              }
            }}
            onError={setError}
          />
        ))}
      </BlockStack>

      <ScanInput
        label={t("追加する商品（バーコード／SKU）")}
        helpText={t(
          "読み取った商品を数量1で明細に追加します。数量は追加後に発注数の欄で直してください。",
        )}
        validate={validateAddition}
        onResolved={(variant) => {
          const input: Parameters<typeof addPlacedLine>[1] = {
            shopify_variant_id: variant.shopify_variant_id,
            quantity: 1,
          };
          const attempt = persistentIdempotencyAttempt({
            scope: addAttemptScope,
            shopGeneration,
            previous: addAttempt.current,
            fingerprint: JSON.stringify(input),
          });
          addAttempt.current = attempt;
          addMutation.mutate({ input, attempt });
        }}
        disabled={addMutation.isPending}
      />

      <Text as="p" variant="bodySm" tone="subdued">
        {t(
          "発注数は入荷済みの数量より下げられません（入荷済みちょうどまで下げると発注書は入荷済みとして締まります）。行を削除できるのは未入荷の明細だけで、最後の1行は削除できません。単価の変更は諸掛の配賦（実原価）にもそのまま反映されます。",
        )}
      </Text>
    </BlockStack>
  );
}

interface HeaderTextFieldProps {
  label: string;
  value: string | null;
  type?: "text" | "date";
  onCommit: (next: string | null) => void;
}

/** メモ・入荷予定日の blur 確定入力。空欄で確定＝消去（null）。 */
function HeaderTextField({
  label,
  value,
  type = "text",
  onCommit,
}: HeaderTextFieldProps) {
  const [draft, setDraft] = useState(value ?? "");

  const commit = () => {
    const trimmed = draft.trim();
    const current = value ?? "";
    if (trimmed === current) return;
    onCommit(trimmed === "" ? null : trimmed);
  };

  return (
    <TextField
      label={label}
      autoComplete="off"
      type={type}
      value={draft}
      onChange={setDraft}
      onBlur={commit}
    />
  );
}

interface EditableLineRowProps {
  item: PlacedOrderItem;
  canRemove: boolean;
  isBusy: boolean;
  onCommitQty: (qty: number) => void;
  onCommitPrice: (price: string) => void;
  onRemove: () => void;
  onError: (message: string) => void;
}

function EditableLineRow({
  item,
  canRemove,
  isBusy,
  onCommitQty,
  onCommitPrice,
  onRemove,
  onError,
}: EditableLineRowProps) {
  const [qtyDraft, setQtyDraft] = useState(String(item.ordered_qty));
  const [priceDraft, setPriceDraft] = useState(item.unit_price);

  const commitQty = () => {
    const trimmed = qtyDraft.trim();
    if (trimmed === String(item.ordered_qty)) return;
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isInteger(parsed) || String(parsed) !== trimmed || parsed < 1) {
      setQtyDraft(String(item.ordered_qty));
      onError(t("発注数は1以上の整数で入力してください。"));
      return;
    }
    if (parsed < item.received_qty) {
      // backend の床と同じ理由を手前で出す＝422 の往復を待たせない。
      setQtyDraft(String(item.ordered_qty));
      onError(
        t(
          "発注数を入荷済みの数量より小さくできません（{item}: 入荷済み {received}）。",
          {
            item: item.sku ?? item.id,
            received: item.received_qty,
          },
        ),
      );
      return;
    }
    onCommitQty(parsed);
  };

  const commitPrice = () => {
    const trimmed = priceDraft.trim();
    if (trimmed === item.unit_price) return;
    const parsed = Number(trimmed);
    if (trimmed === "" || !Number.isFinite(parsed) || parsed < 0) {
      setPriceDraft(item.unit_price);
      onError(t("仕入単価は0以上の数値で入力してください。"));
      return;
    }
    onCommitPrice(trimmed);
  };

  return (
    <InlineGrid
      columns={{ xs: 1, md: "minmax(0, 1fr) 110px 130px auto" }}
      gap="300"
      alignItems="center"
    >
      <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
        <Text as="span">{item.title ?? item.sku ?? t("（商品名なし）")}</Text>
        {item.sku && (
          <Text as="p" variant="bodySm" tone="subdued">
            SKU: {item.sku}
          </Text>
        )}
      </div>
      <BlockStack gap="100">
        <Text as="span" variant="bodySm" tone="subdued">
          {t("発注数")}
        </Text>
        <TextField
          label={t("{item} の発注数", { item: item.sku ?? item.id })}
          labelHidden
          autoComplete="off"
          type="number"
          min={Math.max(1, item.received_qty)}
          value={qtyDraft}
          onChange={setQtyDraft}
          onBlur={commitQty}
          disabled={isBusy}
          suffix={t("個")}
        />
      </BlockStack>
      <BlockStack gap="100">
        <Text as="span" variant="bodySm" tone="subdued">
          {t("仕入単価")}
        </Text>
        <TextField
          label={t("{item} の仕入単価", { item: item.sku ?? item.id })}
          labelHidden
          autoComplete="off"
          type="number"
          min={0}
          value={priceDraft}
          onChange={setPriceDraft}
          onBlur={commitPrice}
          disabled={isBusy}
        />
      </BlockStack>
      {(item.received_qty > 0 || canRemove) && (
        <InlineStack gap="200" blockAlign="center" wrap>
          {item.received_qty > 0 && (
            <Text as="span" variant="bodySm" tone="subdued">
              {t("入荷済み {received}", { received: item.received_qty })}
            </Text>
          )}
          {canRemove && (
            <Button
              variant="plain"
              tone="critical"
              onClick={onRemove}
              disabled={isBusy}
            >
              {t("行を削除")}
            </Button>
          )}
        </InlineStack>
      )}
    </InlineGrid>
  );
}
